import React, { useState, useEffect } from "react";
import * as Icons from "react-feather";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import axios from "axios";
import SKAlerts, { CreateAlert } from "../../shared/common-alerts/SKAlerts";
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg';
import ClientEnvironmentModel from "./models/ClientEnvironmentModel";
import { getStorageValues } from "../../shared/CommonFunctions";
import { getApiBaseUrl } from "../../utils/apiConfig";
import ClientEnvironmentModal from "./ClientEnvironmentModal";
import { Modal, Button } from "react-bootstrap";
import ClientSettingsEdit from "./components/ClientSettingsEdit";

const LOG_FETCH_LIMIT = 2000;

function ClientEnvironmentGrid(props) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [clientEnvironmentModel, setClientEnvironmentModel] = useState(new ClientEnvironmentModel());
  const [filterText, setFilterText] = React.useState("");
  const [alert, setAlert] = useState(null);
  const [deployStatus, setDeployStatus] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [logsByClient, setLogsByClient] = useState({});
  const [remarkModal, setRemarkModal] = useState({ open: false, row: null, remark: "", commits: [], loading: false, error: null });
  const [actionMenuRowId, setActionMenuRowId] = useState(null);
  const [clientSettingsModal, setClientSettingsModal] = useState({ open: false, loading: false, data: [], error: null });
  const [clientSettingsSaving, setClientSettingsSaving] = useState(false);
  const [clientSettingsContext, setClientSettingsContext] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState({});
  const logRefs = React.useRef({});
  const logTimers = React.useRef({});

  const scrollLogsToBottom = (clientId, delayMs = 0) => {
    const runScroll = () => {
      const el = logRefs.current[clientId];
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    };
    if (delayMs > 0) {
      setTimeout(runScroll, delayMs);
    } else {
      window.requestAnimationFrame(runScroll);
    }
  };

  const toggleActionMenu = (rowId, event) => {
    setActionMenuRowId((prev) => {
      const next = prev === rowId ? null : rowId;
      if (next && event?.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const preferredHeight = 180;
        const direction = spaceBelow < preferredHeight && spaceAbove > spaceBelow ? 'up' : 'down';
        setDropdownDirection((dir) => ({ ...dir, [rowId]: direction }));
      }
      return next;
    });
  };

  const fetchClientSettings = async (ctx) => {
    const context = ctx || clientSettingsContext;
    if (!context?.ClientUrl) {
      setClientSettingsModal(prev => ({ ...prev, loading: false, error: "Client URL is missing for this environment." }));
      return;
    }
    try {
      setClientSettingsModal(prev => ({ ...prev, loading: true, error: null }));
      const res = await axios.get(getApiBaseUrl() + "/clientsettings/getall", {
        params: { ClientUrl: context.ClientUrl }
      });
      if (res?.data?.success) {
        setClientSettingsModal(prev => ({ ...prev, data: res.data.data[0], loading: false }));
      } else {
        setClientSettingsModal(prev => ({ ...prev, loading: false, error: res?.data?.message || "Unable to fetch client settings" }));
      }
    } catch (error) {
      console.error(error);
      setClientSettingsModal(prev => ({ ...prev, loading: false, error: "Unable to fetch client settings" }));
    }
  };

  const openClientSettingsModal = (row) => {
    setActionMenuRowId(null);
    setClientSettingsContext(row || null);
    setClientSettingsModal(prev => ({ ...prev, open: true }));
    fetchClientSettings(row || null);
  };

  const closeClientSettingsModal = () => {
    setClientSettingsModal(prev => ({ ...prev, open: false }));
    setClientSettingsContext(null);
  };

  const updateClientSetting = async (clientSettingsId, value) => {
    if (!clientSettingsContext?.ClientUrl) {
      CreateAlert("Client URL is missing for this environment.", "Error", setAlert);
      return;
    }
    try {
      setClientSettingsSaving(true);
      const payload = {
        ClientSettingsId: clientSettingsId,
        Value: value,
        UserId: getStorageValues('userId')
      };
      const res = await axios.post(getApiBaseUrl() + "/clientsettings/update", payload, {
        params: { ClientUrl: clientSettingsContext.ClientUrl }
      });
      if (res?.data?.success) {
        setClientSettingsModal(prev => ({
          ...prev,
          data: prev.data.map(item => item.ClientSettingsId === clientSettingsId ? { ...item, Value: value } : item)
        }));
        CreateAlert("Client setting updated successfully", "Success", setAlert);
      } else {
        CreateAlert(res?.data?.message || "Unable to update setting", "Error", setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert("Unable to update setting", "Error", setAlert);
    } finally {
      setClientSettingsSaving(false);
    }
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!actionMenuRowId) return;
      const target = event.target;
      if (!target.closest || !target.closest('.client-actions-menu')) {
        setActionMenuRowId(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [actionMenuRowId]);

  const fetchLogs = async (clientId, clientUrl, limit = LOG_FETCH_LIMIT) => {
    try {
      const res = await axios.get(getApiBaseUrl() + "/getDeploymentLogs", {
        params: { clientId, clientUrl, limit }
      });
      if (res.status === 200 && res.data.success) {
        setLogsByClient(prev => ({
          ...prev,
          [clientId]: res.data.logs
        }));
        // Auto-scroll to bottom after logs update
        scrollLogsToBottom(clientId, 50);
      }
    } catch (e) {
      // ignore
    }
  };

  const startPollingLogs = (clientId, clientUrl) => {
    // Clear existing timer if any
    if (logTimers.current[clientId]) {
      clearInterval(logTimers.current[clientId]);
    }
    // Start new interval every 3 seconds
    logTimers.current[clientId] = setInterval(() => {
      fetchLogs(clientId, clientUrl);
    }, 3000);
  };

  const stopPollingLogs = (clientId) => {
    if (logTimers.current[clientId]) {
      clearInterval(logTimers.current[clientId]);
      delete logTimers.current[clientId];
    }
  };

  const handleRemarkChange = (e) => {
    const value = e.target.value;
    setRemarkModal(prev => ({ ...prev, remark: value }));
  };

  function closeModal() {
    setIsOpen(false);
    props.fetchClientEnvironment();
  }

  const rows = props.ClientEnvironmentData
    ? props.ClientEnvironmentData.filter(
      (item) =>
        (item.Name &&
          item.Name.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.ClientUrl &&
          item.ClientUrl.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.DatabaseName &&
          item.DatabaseName.toLowerCase().includes(filterText.toLowerCase()))
    )
    : [];

  const openRemarkModal = (row) => {
    if (deployStatus[row.ClientId]?.percent > 0 && deployStatus[row.ClientId]?.percent < 100) {
      return;
    }
    setRemarkModal({ open: true, row, remark: "", commits: [], loading: true, error: null });
    fetchPendingCommits(row);
  };

  const closeRemarkModal = () => {
    setRemarkModal({ open: false, row: null, remark: "", commits: [], loading: false, error: null });
  };

  const fetchPendingCommits = async (row) => {
    if (!row?.BranchName) {
      setRemarkModal(prev => ({ ...prev, loading: false, error: "Branch name is missing for this environment.", commits: [], remark: "" }));
      return;
    }
    try {
      setRemarkModal(prev => ({ ...prev, loading: true, error: null }));
      const response = await axios.post(getApiBaseUrl() + "/getPendingCommits", {
        branchName: row.BranchName,
      });
      const commits = response?.data?.commits || [];
      const defaultRemark = commits.length ? commits.join("\n").slice(0, 500) : "No new commits detected.";
      setRemarkModal(prev => ({ ...prev, loading: false, commits, remark: defaultRemark }));
    } catch (error) {
      console.error(error);
      setRemarkModal(prev => ({ ...prev, loading: false, error: "Unable to fetch latest commits. You can still add a remark manually." }));
    }
  };

  const handleRemarkSubmit = () => {
    const currentRow = remarkModal.row;
    if (!currentRow) {
      closeRemarkModal();
      return;
    }
    const trimmed = remarkModal.remark.trim();
    if (!trimmed) {
      CreateAlert("Deployment remark is required to start processing.", "Error", setAlert);
      return;
    }
    closeRemarkModal();
    startDeployment(currentRow, trimmed.substring(0, 500));
  };

  const startDeployment = async (row, deploymentRemark) => {
    const id = row.ClientId;
    const databaseName = row.DatabaseName;
    const branchName = row.BranchName;
    const clientUrl = row.ClientUrl;
    const clientApiUrl = row.APIUrl;
    const portNumber = row.PortNumber;
    const applicationId = row.ApplicationId;
    const websiteFolder = row.WebsiteFolder;
    const userId = getStorageValues('userId');
    if (!applicationId && applicationId !== 0) {
      CreateAlert("Application ID is missing for this environment. Please update the record before deploying.", "Error", setAlert);
      return;
    }
    if (!userId) {
      CreateAlert("Unable to resolve the current user. Please sign in again.", "Error", setAlert);
      return;
    }
    const sanitizedRemark = deploymentRemark ? deploymentRemark.trim() : "";
    if (!sanitizedRemark) {
      CreateAlert("Deployment remark is required to start processing.", "Error", setAlert);
      return;
    }
    const remarkPayload = sanitizedRemark.substring(0, 500);
    let versionTag = null;
    // STEP UPDATES
    const update = (percent, text, extra = {}) => {
      setDeployStatus(prev => ({
        ...prev,
        [id]: { ...(prev[id] || {}), percent, text, ...extra }
      }));
    };

    try {
      update(5, "Initializing deployment...", { versionTag: null, remark: remarkPayload });
      // 1) Running SQL Scripts
      update(15, "Running SQL Scripts...");
      setExpandedRows(prev => ({ ...prev, [id]: true }));
      fetchLogs(id, clientUrl);
      startPollingLogs(id, clientUrl);
      const sqlResponse = await axios.post(getApiBaseUrl() + "/runSqlScripts", 
        { 
          clientId: id,
          databaseName: databaseName,
          branchName: branchName,
          clientUrl: clientUrl,
          applicationId: applicationId,
          userId: userId,
          remark: remarkPayload
        });
      versionTag = sqlResponse?.data?.versionTag || null;
      const sqlStatusText = versionTag ? `SQL scripts completed · ${versionTag}` : "SQL scripts completed.";
      update(40, sqlStatusText, versionTag ? { versionTag } : {});
      fetchLogs(id, clientUrl);

       // 2) Deploy Backend
       update(55, "Deploying Backend API...");
       const backendResponse = await axios.post(getApiBaseUrl() + "/deployBackend", 
        { 
          portNumber: portNumber,
          clientId: id,
          clientUrl: clientUrl,
          clientApiUrl: clientApiUrl,
          versionTag: versionTag
        });
      const backendData = backendResponse?.data;
      if (backendData?.skipped) {
        const ownerLabel = backendData.ownerClientUrl || `ClientId ${backendData.ownerClientId}`;
        update(60, `Skipping backend (shared API — deploy ${ownerLabel} to update API)`);
      }
      fetchLogs(id, clientUrl);

       // 3) Build Frontend
       update(75, "Building Frontend...");
       await axios.post(getApiBaseUrl() + "/buildFrontend", { 
          clientId: id,
          clientUrl: clientUrl,
          clientApiUrl: clientApiUrl,
          applicationId: applicationId,
         versionTag: versionTag,
         websiteFolder: websiteFolder,
         branchName: branchName,
         remark: remarkPayload
       });
      fetchLogs(id, clientUrl);

       // 4) Upload Build
       update(90, "Uploading UI Build...");
       await axios.post(getApiBaseUrl() + "/uploadFrontend", { 
          clientId: id,
          clientUrl: clientUrl,
         versionTag: versionTag,
         websiteFolder: websiteFolder
       });
      fetchLogs(id, clientUrl);

      // Completed
      const completionText = versionTag ? `Deployment Completed · ${versionTag}` : "Deployment Completed.";
      const completionMeta = { versionTag: versionTag || null };
      if (remarkPayload) {
        completionMeta.remark = remarkPayload;
      }
      update(100, completionText, completionMeta);
      fetchLogs(id, clientUrl);
      stopPollingLogs(id);
      scrollLogsToBottom(id);
      const successMessage = versionTag ? `Deployment finished successfully! Version ${versionTag}` : "Deployment finished successfully!";
      CreateAlert(successMessage, "Success", setAlert);

    } catch (err) {
      const failureMeta = {};
      if (remarkPayload) {
        failureMeta.remark = remarkPayload;
      }
      update(0, "Failed.", failureMeta);
      CreateAlert("Deployment failed!", "Error", setAlert);
      console.error(err);
      stopPollingLogs(id);
      scrollLogsToBottom(id);
    }
  };

  const openActionType = (row, name) => {
    setClientEnvironmentModel(row);
    setIsOpen(true);
    setActionType(name);
  };
  async function onSubmit(e, data) {
    const clientEnvironmentModel = data;
    if (e.target.name === "Edit") {
      let clientEnvironmentDetails = {
        ClientId: clientEnvironmentModel.ClientId,
        Name: clientEnvironmentModel.Name,
        APIUrl: clientEnvironmentModel.APIUrl,
        ClientUrl: clientEnvironmentModel.ClientUrl,
        PortNumber: clientEnvironmentModel.PortNumber !== undefined && clientEnvironmentModel.PortNumber !== null && `${clientEnvironmentModel.PortNumber}`.trim() !== "" ? Number(clientEnvironmentModel.PortNumber) : null,
        BranchName: clientEnvironmentModel.BranchName,
        DatabaseName: clientEnvironmentModel.DatabaseName,
        FaviconPath: clientEnvironmentModel.FaviconPath,
        WebsiteFolder: clientEnvironmentModel.WebsiteFolder ? clientEnvironmentModel.WebsiteFolder : null,
        ApplicationId: clientEnvironmentModel.ApplicationId !== undefined && clientEnvironmentModel.ApplicationId !== null && `${clientEnvironmentModel.ApplicationId}`.trim() !== "" ? Number(clientEnvironmentModel.ApplicationId) : null,
        UserId: getStorageValues('userId')
      };
      if (validation(clientEnvironmentDetails)) {
        try {
          const response = await axios.post(
            getApiBaseUrl() + "/saveClientEnvironmentDetails",
            clientEnvironmentDetails
          );
          if (response.status === 200) {
            if (response.data.data[0].success === 1) {
              CreateAlert(response.data.data[0].message, "Success", setAlert);
              props.fetchClientEnvironment();
              closeModal();
            } else {
              CreateAlert(response.data.data[0].message, "Warning", setAlert);
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    } else if (e.target.name === "Delete") {
      let clientEnvironmentDetails = {
        ClientId: clientEnvironmentModel.ClientId
      };
      try {
        const response = await axios.post(
          getApiBaseUrl() + "/deleteClientEnvironment", clientEnvironmentDetails
        );
        if (response.status === 200) {
          CreateAlert("Client environment deleted successfully!!", "Success", setAlert);
          props.fetchClientEnvironment();
          closeModal();
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  function validation(clientEnvironmentDetails) {
    var validation = true;
    if (clientEnvironmentDetails) {
      if (clientEnvironmentDetails.Name !== null) {
        if (clientEnvironmentDetails.Name.length === 0) {
          CreateAlert("Please enter client name!!", "Warning", setAlert);
          validation = false;
        }
      } else {
        CreateAlert("Please enter client name!!", "Warning", setAlert);
        validation = false;
      }
    } else {
      CreateAlert("Some proplem fatching user details entered by the user!!", "Error", setAlert);
      return (validation = false);
    }
    return validation;
  }

  const columns = [
    {
      name: <div className="titlename"><span>Client Name</span></div>,
      selector: row => row.Name,
      cell: (row) => (
        <span title={row.Name}>{row.Name}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Client Url </span></div>,
      selector: row => row.ClientUrl,
      cell: (row) => (
        <a
          href={(row.ClientUrl || '').startsWith('http') ? row.ClientUrl : `https://${row.ClientUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          title={row.ClientUrl}
          style={{ color: '#0d6efd', textDecoration: 'underline' }}
        >
          {row.ClientUrl}
        </a>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Branch </span></div>,
      selector: row => row.BranchName,
      cell: (row) => (
        <span title={row.BranchName}>{row.BranchName}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Database </span></div>,
      selector: row => row.DatabaseName,
      cell: (row) => (
        <span title={row.DatabaseName}>{row.DatabaseName}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
  ];
  columns.push({
    name: <div className="titlename">Deployment</div>,
    cell: (row) => {
      const state = deployStatus[row.ClientId] || { percent: 0, text: "Not started" };

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "250px" }}>

          {/* LEFT SIDE – Progress Bar + Text */}
          <div style={{ width: "120px" }}>
            <div
              style={{
                height: "8px",
                width: "100%",
                background: "#eee",
                borderRadius: "4px",
                overflow: "hidden",
                marginBottom: "5px"
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${state.percent}%`,
                  background: state.percent === 100 ? "#4caf50" : "#2196f3",
                  transition: "0.4s ease"
                }}
              />
            </div>

            <div style={{ fontSize: "11px", textAlign: "center" }}>
              {state.text}
            </div>
            {state.versionTag ? (
              <div style={{ fontSize: "10px", textAlign: "center", marginTop: "2px", color: "#555" }}>
                Version: {state.versionTag}
              </div>
            ) : null}
          </div>

          {/* RIGHT SIDE – BUTTON */}
          <button
            className="btn btn-sm btn-success"
            disabled={state.percent > 0 && state.percent < 100}
            onClick={() => openRemarkModal(row)}
            style={{ display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}
          >
            <Icons.Play size={16} />
            {state.percent > 0 && state.percent < 100 ? "Processing..." : "Process"}
          </button>
        </div>
      );
    },
  });
  if (props.UserPermissions.ViewModule === 1 || props.UserPermissions.EditModule === 1 || props.UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          <div className={`dropdown d-inline-block client-actions-menu ${ (dropdownDirection[row.ClientId] || 'down') === 'up' ? 'dropup' : '' }`}>
            <Link
              className="btn btn-link p-2 ms-2"
              onClick={(e) => toggleActionMenu(row.ClientId, e)}
              aria-expanded={actionMenuRowId === row.ClientId}
              aria-label="More actions"
              style={{ boxShadow: 'none' }}
            >
              <Icons.MoreVertical size={16} />
            </Link>
            <div
              className={`dropdown-menu dropdown-menu-end ${actionMenuRowId === row.ClientId ? 'show' : ''}`}
              style={(dropdownDirection[row.ClientId] || 'down') === 'up'
                ? { right: 0, left: 'auto', bottom: '100%', top: 'auto' }
                : { right: 0, left: 'auto' }
              }
            >
              {
                props.UserPermissions.EditModule === 1 ? (
                  <Link
                    className="dropdown-item d-flex align-items-center gap-2"
                    to=""
                    onClick={() => {
                      setActionMenuRowId(null);
                      openActionType(row, 'Edit');
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#5e9ef8ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                  >
                    <Icons.Edit size={14} />
                      Edit
                  </Link>
                ) : null
              }
              {
                props.UserPermissions.DeleteModule === 1 ? (
                  <Link
                    className="dropdown-item d-flex align-items-center gap-2"
                    to=""
                    onClick={() => {
                      setActionMenuRowId(null);
                      openActionType(row, 'Delete');
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fc7b6dff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                  >
                    <Icons.Trash2 size={14} />
                      Delete
                    </Link>
                ) : null
              }
              <Link
                className="dropdown-item"
                to=""
                onClick={() => {
                  openClientSettingsModal(row);
                }}
              >
                Client Settings
              </Link>
            </div>
          </div>
        </div>
      ),
      center: "true",
      width: "150px"
    },);
  };


  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="Name"
            sortIcon={<SortArrow />}
            persistTableHead={true}
            expandableRows
            expandableRowExpanded={row => !!expandedRows[row.ClientId]}
            onRowExpandToggled={(expanded, row) => {
              setExpandedRows(prev => ({ ...prev, [row.ClientId]: expanded }));
              if (expanded) fetchLogs(row.ClientId, row.ClientUrl);
            }}
            expandableRowsComponent={({ data }) => {
              const logs = logsByClient[data.ClientId] || [];
              const renderLine = (line, idx) => {
                const lower = line.toLowerCase();
                const isError = lower.includes('error') || lower.includes('[git][stderr]');
                const isWarn = lower.includes('warn') || lower.includes('warning');
                const style = isError
                  ? { background: '#ffe6e6' }
                  : isWarn
                  ? { background: '#fff7e6' }
                  : {};
                return (
                  <div key={idx} style={style}>{line}</div>
                );
              };
              return (
                <div style={{ padding: "10px", background: "#fafafa" }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Deployment Logs</div>
                  <pre ref={el => { logRefs.current[data.ClientId] = el; }} style={{
                      maxHeight: 240,
                      overflow: "auto",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      wordBreak: "break-word"
                    }}>
                    {logs.length ? logs.map(renderLine) : "No logs yet..."}
                  </pre>
                </div>
              );
            }}
          />
        </div>
      </div>
      <ClientEnvironmentModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={clientEnvironmentModel}
        onSubmit={onSubmit}
      />
      <Modal show={remarkModal.open} onHide={closeRemarkModal} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Deployment Remark</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Review the latest commits detected on the selected branch, then confirm or edit the deployment description.</p>
          {remarkModal.loading ? (
            <div className="alert alert-info">Fetching commits…</div>
          ) : null}
          {remarkModal.error ? (
            <div className="alert alert-warning">{remarkModal.error}</div>
          ) : null}
          {!remarkModal.loading && remarkModal.commits.length > 0 ? (
            <div className="mb-3" style={{ maxHeight: 160, overflow: "auto", background: "#f8f9fa", padding: "10px", borderRadius: "4px" }}>
              {remarkModal.commits.map((commit, idx) => (
                <div key={idx} style={{ fontFamily: "monospace", fontSize: "12px" }}>{commit}</div>
              ))}
            </div>
          ) : null}
          <textarea
            className="form-control"
            rows={4}
            maxLength={500}
            value={remarkModal.remark}
            onChange={handleRemarkChange}
            placeholder="Example: Release 1.12.5 for Acme Corp"
          />
          <div className="text-end text-muted" style={{ fontSize: "12px" }}>
            {remarkModal.remark.length}/500
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRemarkModal}>Cancel</Button>
          <Button variant="primary" onClick={handleRemarkSubmit} disabled={remarkModal.loading || !remarkModal.remark.trim()}>Continue</Button>
        </Modal.Footer>
      </Modal>
      <ClientSettingsEdit
        show={clientSettingsModal.open}
        onClose={closeClientSettingsModal}
        settings={clientSettingsModal.data}
        loading={clientSettingsModal.loading || clientSettingsSaving}
        error={clientSettingsModal.error}
        onRefresh={fetchClientSettings}
        onUpdate={updateClientSetting}
      />
    </>
  );
}

export default ClientEnvironmentGrid;
